import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { DeleteCard } from "api/paymentAPI";
import { RefundPayment } from "api/paymentAPI";
import { GetParkingShortlyDetails } from "api/paymentAPI";
import { GenerateClientSecret } from "api/paymentAPI";
import { PostParkingCardPayment } from "api/paymentAPI";
import { PostUnitParkingCardPayment } from "api/paymentAPI";
import { UpdateParkingAfter3dSecure } from "api/paymentAPI";
import { UpdatePaymentMethodAPI } from "api/paymentAPI";
import { GetDockTransferInfo } from "api/paymentAPI";
import { RetryDockRevenueTransfer } from "api/paymentAPI";
import { UpdateACHSubscriptionBeforePayment } from "api/paymentAPI";
import { PostACHPayment } from "api/paymentAPI";
import { GetShortlyDetails } from "api/paymentAPI";
import { GetCardDetails } from "api/paymentAPI";
import { PostCardPayment } from "api/paymentAPI";
import { GetAuthorizenetPaymentMethods } from "api/paymentAPI";
import { UpdateSubscriptionAfter3dSecure } from "api/paymentAPI";
import { CreateSetupIntentACH } from "api/paymentAPI";
import { get } from "lodash";

const handleThunkError = (err, rejectWithValue) => {
  return rejectWithValue(
    err?.response?.data || {
      success: false,
      message: "Something went wrong",
    },
  );
};

const initialState = {
  cardDetails: [],
  shortlyData: {},
  placeId: null,
  paymentMethodsLoading: false,
  loading: false,
};

export const getShortlyData = createAsyncThunk(
  "payment/getShortlyData",
  async (id, { rejectWithValue }) => {
    try {
      const response = await GetShortlyDetails(id);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const getAuthorizenetPaymentMethods = createAsyncThunk(
  "payment/authorizenet/fetchPaymentMethods",
  async ({ customerId, placeId }, { rejectWithValue }) => {
    try {
      const response = await GetAuthorizenetPaymentMethods(customerId, placeId);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const getParkingShortlyData = createAsyncThunk(
  "payment/getParkingShortlyData",
  async (id, { rejectWithValue }) => {
    try {
      const response = await GetParkingShortlyDetails(id);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const postCardPayment = createAsyncThunk(
  "payment/postCardPayment",
  async (data, { rejectWithValue }) => {
    try {
      const response = await PostCardPayment(data);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const postParkingCardPayment = createAsyncThunk(
  "payment/parking/postParkingCardPayment",
  async (data, { rejectWithValue }) => {
    try {
      const response = await PostParkingCardPayment(data);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const postUnitParkingCardPayment = createAsyncThunk(
  "payment/parking/postUnitParkingCardPayment",
  async (data, { rejectWithValue }) => {
    try {
      const response = await PostUnitParkingCardPayment(data);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const getCardDetails = createAsyncThunk(
  "payment/getCardDetails",
  async ({ id, placeId }, { rejectWithValue }) => {
    try {
      const response = await GetCardDetails(id, placeId);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const deleteCard = createAsyncThunk(
  "payment/deleteCard",
  async ({ id, placeId }, { rejectWithValue }) => {
    try {
      const response = await DeleteCard(id, placeId);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const paymentRefund = createAsyncThunk(
  "payment/paymentRefund",
  async (data, { rejectWithValue }) => {
    try {
      const response = await RefundPayment(data);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const postACHPayment = createAsyncThunk(
  "payment/postACHPayment",
  async (data, { rejectWithValue }) => {
    try {
      const response = await PostACHPayment(data);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const generateClientSecret = createAsyncThunk(
  "payment/generateClientSecret",
  async (data, { rejectWithValue }) => {
    try {
      const response = await GenerateClientSecret(data);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const updateSubscriptionAfter3dSecure = createAsyncThunk(
  "payment/updateSubscriptionAfter3dSecure",
  async (data, { rejectWithValue }) => {
    try {
      const response = await UpdateSubscriptionAfter3dSecure(data);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const updateParkingAfter3dSecure = createAsyncThunk(
  "payment/updateParkingAfter3dSecure",
  async (data, { rejectWithValue }) => {
    try {
      const response = await UpdateParkingAfter3dSecure(data);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const createSetupIntentACH = createAsyncThunk(
  "payment/createSetupIntentACH",
  async (data, { rejectWithValue }) => {
    try {
      const response = await CreateSetupIntentACH(data);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const updateACHSubscription = createAsyncThunk(
  "payment/updateACHSubscription",
  async (data, { rejectWithValue }) => {
    try {
      const response = await UpdateACHSubscriptionBeforePayment(data);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const updateStripePaymentMethod = createAsyncThunk(
  "payment/updatePaymentMethod",
  async (data, { rejectWithValue }) => {
    try {
      const response = await UpdatePaymentMethodAPI(data);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const dockRevenueTransfer = createAsyncThunk(
  "payment/dockRevenueTransfer",
  async (data, { rejectWithValue }) => {
    try {
      const response = await RetryDockRevenueTransfer(data);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const dockTransferInfo = createAsyncThunk(
  "payment/dockTransferInfo",
  async (data, { rejectWithValue }) => {
    try {
      const response = await GetDockTransferInfo(data);
      return response.data;
    } catch (err) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const paymentSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {
    restConsultationStore: (state) => {
      state.cardDetails = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCardDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCardDetails.fulfilled, (state, { payload }) => {
        state.cardDetails = get(payload, "paymentMethods", []);
        state.placeId = get(payload, "placeId", null);
        state.loading = false;
      })
      .addCase(getCardDetails.rejected, (state) => {
        state.loading = false;
      })
      .addCase(deleteCard.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCard.fulfilled, (state, { payload }) => {
        state.cardDetails = get(payload, "data", []);
        state.loading = false;
      })
      .addCase(deleteCard.rejected, (state) => {
        state.loading = false;
      })
      .addCase(dockTransferInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(dockTransferInfo.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(dockTransferInfo.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { restConsultationStore } = paymentSlice.actions;
export default paymentSlice.reducer;
